<template>
  <section class="track-section" aria-label="Track list">
    <div v-if="items.length === 0" class="empty-state">
      <p>No local tracks loaded.</p>
      <span>Use the audio button below to add music files.</span>
    </div>

    <ol v-else ref="listEl" class="track-list">
      <li
        v-for="(item, index) in items"
        :key="item.name || index"
        class="track-row"
        :class="{
          current: currentItem === item,
          selected: cursorIndex === index,
        }"
      >
        <button
          class="queue-button"
          type="button"
          :aria-label="queueLabel(index)"
          @click.stop="toggleQueue(index)"
        >
          <i
            class="fa"
            :class="playQueueSet.has(index) ? 'fa-minus' : 'fa-plus'"
            aria-hidden="true"
          ></i>
        </button>

        <button
          class="track-name"
          type="button"
          @click="$emit('set-current', index)"
        >
          <span class="track-number">{{ formatIndex(index) }}</span>
          <span class="track-title">{{ formatTrackName(item.name) }}</span>
        </button>
      </li>
    </ol>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits, defineExpose */
import { ref } from "vue";

const MOBILE_BREAKPOINT = 860;
const ITEM_HEIGHT = 52;
const MOBILE_ITEM_HEIGHT = 42;

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  currentItem: {
    type: Object,
    default: null,
  },
  cursorIndex: {
    type: Number,
    default: 0,
  },
  playQueueSet: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["set-current", "push-queue", "pop-queue"]);
const listEl = ref(null);

function formatTrackName(name = "") {
  return name.replace(/\.(mp3|wav|ogg|webm)$/i, "");
}

function formatIndex(index) {
  return String(index + 1).padStart(2, "0");
}

function queueLabel(index) {
  return props.playQueueSet.has(index) ? "Remove from queue" : "Add to queue";
}

function toggleQueue(index) {
  emit(props.playQueueSet.has(index) ? "pop-queue" : "push-queue", index);
}

function scrollToIndex(index) {
  const itemHeight =
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
      ? MOBILE_ITEM_HEIGHT
      : ITEM_HEIGHT;
  listEl.value?.scrollTo({
    top: index * itemHeight - listEl.value.clientHeight / 2,
  });
}

defineExpose({
  scrollToIndex,
});
</script>

<style scoped lang="less">
.track-section {
  position: relative;
  z-index: 1;
  min-height: 190px;
  flex: 1;
  overflow: hidden;
}

.track-list {
  max-height: 570px;
  overflow: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.11);
}

.track-row {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  min-height: 52px;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  color: #c8c1be;
}

.track-row.selected {
  background: rgba(255, 255, 255, 0.055);
}

.track-row.current {
  color: #f8f2ee;
}

.queue-button,
.track-name {
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.queue-button {
  width: 30px;
  height: 30px;
  color: #928b88;
}

.queue-button:hover,
.queue-button:focus-visible,
.track-name:hover,
.track-name:focus-visible {
  color: #ffffff;
  outline: none;
}

.track-name {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: center;
  text-align: left;
}

.track-number {
  color: #8c8581;
  font-size: 12px;
}

.track-title {
  overflow: hidden;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.empty-state {
  display: flex;
  min-height: 170px;
  flex-direction: column;
  justify-content: center;
  border-top: 1px solid rgba(255, 255, 255, 0.11);
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  color: #c8c1be;
}

.empty-state p {
  margin-bottom: 10px;
  color: #f7f2ee;
  font-size: 18px;
}

.empty-state span {
  color: #8f8987;
  font-size: 13px;
}

@media (max-width: 860px) {
  .track-list {
    max-height: 200px;
  }

  .track-row {
    grid-template-columns: 32px minmax(0, 1fr);
    min-height: 42px;
  }

  .queue-button {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }

  .track-name {
    grid-template-columns: 40px minmax(0, 1fr);
  }

  .track-number,
  .track-title {
    font-size: 11px;
  }
}
</style>
